import React from "react";

/**
 * Template #7 — timeline_marker (3D Isometric)
 * Sequences, Phases, Versions, Iterations
 * Parts: marker_1, marker_2, marker_3
 * Actions: highlight, advance, fade_previous
 *
 * 3D raised platform markers connected by a glowing rail.
 */

export interface TimelineMarkerProps {
  markers?: string[];
  activeIndex?: number;
  completedIndices?: number[];
  fadedIndices?: number[];
  opacity?: number;
  accentColor?: string;
}

export const TimelineMarker: React.FC<TimelineMarkerProps> = ({
  markers = ["Phase 1", "Phase 2", "Phase 3"],
  activeIndex,
  completedIndices = [],
  fadedIndices = [],
  opacity = 1,
  accentColor = "#06b6d4",
}) => {
  const count = markers.length;
  const startX = 120;
  const endX = 680;
  const y = 210;
  const spacing = count > 1 ? (endX - startX) / (count - 1) : 0;

  const r = 20;       // marker radius
  const depth = 8;    // 3D depth

  const uid = "tm";

  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id={`${uid}-rail`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#475569" />
          <stop offset="100%" stopColor="#1e293b" />
        </linearGradient>
        <linearGradient id={`${uid}-active`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={accentColor} />
          <stop offset="100%" stopColor="#0f172a" />
        </linearGradient>
        <radialGradient id={`${uid}-sphere`} cx="35%" cy="30%">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.3" />
          <stop offset="50%" stopColor={accentColor} />
          <stop offset="100%" stopColor="#0f172a" />
        </radialGradient>
        <radialGradient id={`${uid}-sphere-off`} cx="35%" cy="30%">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.1" />
          <stop offset="50%" stopColor="#475569" />
          <stop offset="100%" stopColor="#1e293b" />
        </radialGradient>
      </defs>

      {/* Rail shadow */}
      <line x1={startX} y1={y + depth + 3} x2={endX} y2={y + depth + 3} stroke="#000" strokeWidth={8} opacity={0.1} strokeLinecap="round" />

      {/* Background rail — 3D bar */}
      <line x1={startX} y1={y + depth} x2={endX} y2={y + depth} stroke="#0f172a" strokeWidth={6} strokeLinecap="round" />
      <line x1={startX} y1={y} x2={endX} y2={y} stroke={`url(#${uid}-rail)`} strokeWidth={6} strokeLinecap="round" />
      {/* Rail specular */}
      <line x1={startX + 4} y1={y - 2} x2={endX - 4} y2={y - 2} stroke="#fff" strokeWidth={1} opacity={0.05} strokeLinecap="round" />

      {/* Progress glow rail */}
      {activeIndex !== undefined && activeIndex >= 0 && (
        <g>
          <line x1={startX} y1={y} x2={startX + spacing * activeIndex} y2={y} stroke={accentColor} strokeWidth={6} strokeLinecap="round" />
          <line x1={startX} y1={y} x2={startX + spacing * activeIndex} y2={y} stroke="#fff" strokeWidth={2} opacity={0.15} strokeLinecap="round" />
        </g>
      )}

      {/* Markers */}
      {markers.map((label, i) => {
        const mx = startX + spacing * i;
        const isActive = i === activeIndex;
        const isCompleted = completedIndices.includes(i);
        const isFadedM = fadedIndices.includes(i);
        const mOp = isFadedM ? 0.35 : 1;

        return (
          <g key={i} opacity={mOp}>
            {/* Platform shadow */}
            <ellipse cx={mx} cy={y + depth + 6} rx={r + 4} ry={5} fill="#000" opacity={isActive ? 0.25 : 0.1} />

            {/* 3D platform disc (bottom) */}
            <ellipse cx={mx} cy={y + depth} rx={r + 2} ry={r * 0.35} fill="#0f172a" stroke={isActive || isCompleted ? accentColor : "#475569"} strokeWidth={1.2} />

            {/* Active glow */}
            {isActive && (
              <g>
                <circle cx={mx} cy={y} r={r + 12} fill={accentColor} opacity={0.06} />
                <circle cx={mx} cy={y} r={r + 6} fill="none" stroke={accentColor} strokeWidth={1.5} opacity={0.2}>
                  <animate attributeName="r" values={`${r + 4};${r + 12};${r + 4}`} dur="2s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.25;0.08;0.25" dur="2s" repeatCount="indefinite" />
                </circle>
              </g>
            )}

            {/* Sphere marker — 3D ball */}
            <circle
              cx={mx}
              cy={y}
              r={r}
              fill={isActive || isCompleted ? `url(#${uid}-sphere)` : `url(#${uid}-sphere-off)`}
              stroke={isActive || isCompleted ? accentColor : "#475569"}
              strokeWidth={1.5}
            />

            {/* Specular highlight dot */}
            <circle cx={mx - r * 0.25} cy={y - r * 0.3} r={r * 0.18} fill="#fff" opacity={isActive ? 0.35 : 0.1} />

            {/* Checkmark for completed */}
            {isCompleted && !isActive && (
              <path
                d={`M${mx - 5},${y + 1} L${mx - 1},${y + 5} L${mx + 7},${y - 4}`}
                fill="none"
                stroke="#fff"
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}

            {/* Number */}
            {!isCompleted && (
              <text
                x={mx}
                y={y + 1}
                fill={isActive ? "#fff" : "#94a3b8"}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={12}
                fontWeight={700}
                fontFamily="'Inter', system-ui, sans-serif"
              >
                {i + 1}
              </text>
            )}

            {/* Label */}
            <text
              x={mx}
              y={y + r + depth + 18}
              fill={isActive ? "#f1f5f9" : isFadedM ? "#64748b" : "#94a3b8"}
              textAnchor="middle"
              fontSize={12}
              fontWeight={isActive ? 600 : 400}
              fontFamily="'Inter', system-ui, sans-serif"
            >
              {label}
            </text>
          </g>
        );
      })}
    </g>
  );
};
